import { AlertTriangle, CheckCircle2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatToBRL } from "@/utils/formatToMoney";

import type { IDepositDraftSummary } from "src/@types/deposit-reconciliation";

interface CommitModalProps {
  open: boolean;
  drafts: IDepositDraftSummary[];
  storeLabel: string;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function CommitModal({
  open,
  drafts,
  storeLabel,
  isLoading,
  onCancel,
  onConfirm,
}: CommitModalProps) {
  const total =
    drafts.reduce((s, d) => s + Math.round(d.totalAmount * 100), 0) / 100;
  const totalNsus = drafts.reduce((s, d) => s + d.nsuCount, 0);
  const totalDeposits = drafts.reduce((s, d) => s + d.depositCount, 0);

  const handleOpenChange = (next: boolean) => {
    if (!next && !isLoading) onCancel();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#2B0C1A]" />
            Confirmar conciliação
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Você vai confirmar{" "}
                <strong>
                  {drafts.length} rascunho{drafts.length !== 1 ? "s" : ""}
                </strong>{" "}
                como conciliaç{drafts.length !== 1 ? "ões" : "ão"} oficial
                {drafts.length !== 1 ? "is" : ""} para a loja{" "}
                <strong>
                  {storeLabel}
                </strong>
                .
              </p>

              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-1.5">Rascunho</th>
                      <th className="text-right px-2 py-1.5">NSUs</th>
                      <th className="text-right px-2 py-1.5">Dep</th>
                      <th className="text-right px-2 py-1.5">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drafts.map(d => (
                      <tr key={d.id} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-mono">#{d.id}</td>
                        <td className="px-2 py-1.5 text-right">{d.nsuCount}</td>
                        <td className="px-2 py-1.5 text-right">
                          {d.depositCount}
                        </td>
                        <td className="px-2 py-1.5 text-right font-medium">
                          {formatToBRL(d.totalAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr className="border-t border-slate-200">
                      <td className="px-2 py-1.5">Total</td>
                      <td className="px-2 py-1.5 text-right">{totalNsus}</td>
                      <td className="px-2 py-1.5 text-right">{totalDeposits}</td>
                      <td className="px-2 py-1.5 text-right">
                        {formatToBRL(total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Esta ação é irreversível pelo analista. Apenas administradores
                  podem desfazer uma conciliação ativa com motivo registrado.
                </span>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isLoading}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-[#2B0C1A] hover:bg-[#4B303C] text-white"
          >
            {isLoading
              ? "Conciliando…"
              : `Confirmar ${drafts.length} conciliaç${drafts.length !== 1 ? "ões" : "ão"}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
